<template>
  <div>
    <MainMenu v-if="menuReady" />
    <GoogleMap
      v-if="sharedMap"
      :load-on-idle="false"
      data-testid="map"
    />
    <aside>
      <MapBanner />
    </aside>
    <aside>
      <GPSButton v-if="isMobile" />
    </aside>
    <aside>
      <FeatureGrid />
    </aside>
    <aside>
      <LinkedLabelDialog
        v-if="linkedLabelFeature"
        :feature="linkedLabelFeature"
        :label="linkedLabelToEdit"
        @close="closeLinkedLabelDialog"
      />
    </aside>
  </div>
</template>

<script>
// Services and Helpers
import {mapState, mapActions, mapMutations} from 'vuex';
import {createCollection, destroyCollection} from '@/lib/filters/filters';
import {createAuditRecord} from '@/services/BackendService';
import {isActiveSession, createSession} from '@/services/SessionService';
import {EventService} from '@/services/EventService';
import {MapService, Markers} from '@/services/MapService';
import {isMobile} from '@/lib/ReusableFunctions/User';
import {Concealments} from '@/services/ConcealmentService';
import {getSubcollection} from '@/services/FirestoreService';

// Components
import GoogleMap from '@/components/GoogleMap.vue';
import MainMenu from '@/components/MainMenu.vue';
import GPSButton from '@/components/GPSButton.vue';
import MapBanner from '@/components/Banner/MapBanner.vue';
import FeatureGrid from '@/components/DataTable/FeatureGrid.vue';
import LinkedLabelDialog from '@/components/InfoWindow/sections/Toolbar/LinkedLabelDialog.vue';

export default {
  name: 'ExternalMapView',
  components: {
    GoogleMap,
    MainMenu,
    GPSButton,
    MapBanner,
    FeatureGrid,
    LinkedLabelDialog,
  },
  data() {
    return {
      isMobile: isMobile(),
      // TODO: Rethink how to handle menu readiness and unmounting. Without this, the menu attempts to
      // do things like subscribe to collections before they are created, leading to errors.
      // To see the issue: remove logic concerning 'menuReady', open the SMAPS dashboard and select a map,
      // wait for map to load, then click 'Home' to return to the dashboard, and then finally select a map again.
      // Observe the errors in the console.
      menuReady: false,
    };
  },
  computed: {
    ...mapState(['sharedMap', 'isEditableMap', 'linkedLabelFeature', 'linkedLabelToEdit']),
  },
  async mounted() {
    // Load Features from Firestore subcollection
    const features = await getSubcollection(
      'sharedMaps',
      this.sharedMap.id,
      'features',
      true,
    );

    // Safely set document title (guard against test environment teardown)
    if (typeof window.document !== 'undefined') {
      window.document.title = this.isEditableMap ? 'Shareable Maps' : 'Beyond Maps';
    }

    const map = MapService.getMap();
    Concealments.setMap(map);

    // Features
    const featuresCollection = createCollection('features', (f) => f.id);
    featuresCollection?.subscribe(null, ({payload}) => {
      if (payload.added?.length) {
        payload.added.forEach((f) => f.setRender(true));
      }
      if (payload.removed?.length) {
        payload.removed.forEach((f) => f.setRender(false));
      }
    });

    // Overlays collection is needed for the toolbox
    const overlaysCollection = createCollection('overlays', (o) => o.id);
    overlaysCollection?.subscribe(null, ({payload}) => {
      if (payload.added?.length) {
        payload.added.forEach((o) => o.setRender(true));
      }
      if (payload.removed?.length) {
        payload.removed.forEach((o) => o.setRender(false));
      }
    });

    // Subscribe store to changes
    await this.subscribeCollections();

    // This must be placed here to avoid issues with calling 'addMenuClickListener()' before the map is ready
    // This specifically occurs in 'FeatureResultCard.vue', which propagates up to 'MainMenu.vue'
    this.menuReady = true;

    const initFeatures = async () => {
      await this.sharedMap.initializeFeatures(features);
      this.setSelectedMenuItem('sharedItemMenu');
      MapService.removeMapListener('loadSharedMapFeatures'); // safety-net
    };

    if (map.getProjection()) {
      initFeatures();
    } else {
      MapService.addMapListener('loadSharedMapFeatures', 'idle', initFeatures);
    }
    if (process.env.VUE_APP_RECAPTCHA_SITE_KEY) {
      this.auditSession();
    }
  },
  unmounted() {
    this.menuReady = false;
    Markers.clearOverlays();
    destroyCollection('features');
    destroyCollection('overlays');
    EventService.removeAllHandlers();
    // Remove any pending map listeners to prevent callbacks after unmount
    MapService.removeMapListener('loadSharedMapFeatures');
    this.clearSharedMap();
    this.clearUserLayers();
  },
  methods: {
    ...mapMutations(['setSelectedMenuItem', 'clearSharedMap', 'setLinkedLabelFeature', 'setLinkedLabelToEdit']),
    ...mapActions('collections', ['subscribeCollections']),
    ...mapActions('layers', ['clearUserLayers']),
    closeLinkedLabelDialog() {
      this.setLinkedLabelFeature(null);
      this.setLinkedLabelToEdit(null);
    },
    auditSession() {
      const mode = this.isEditableMap ? 'edit' : 'view';
      const sessionId = `${this.sharedMap.id}-${mode}`;
      // Sessions expire after 30 minutes and are renewed on page refresh
      if (!isActiveSession(sessionId)) {
        createSession(sessionId);
        // Create an audit record for the session
        const mode = this.isEditableMap ? 'edit' : 'view';
        createAuditRecord(`${mode}ModeAccess`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
